// utilies/other/MeasureArea.js
// Cesium polygon area tool: click to add vertices, live area update with mouse move.
// Finish: Right-click OR Enter => RESET (remove drawings) + exit
// Cancel: Esc => RESET + exit
// On destroy (leaving mode): FULL reset (remove entities + remove all listeners)

import * as Cesium from "cesium";

export function createMeasureAreaTool(viewer, { onFinish } = {}) {
  if (!viewer?.scene?.canvas) throw new Error("viewer is invalid");

  const scene = viewer.scene;
  const canvas = scene.canvas;
  const handler = new Cesium.ScreenSpaceEventHandler(canvas);

  let committed = [];
  let floating = null;

  let polygonEntity = null;
  let outlineEntity = null;
  let labelEntity = null;

  let ended = false;

  const getLivePositions = () => {
    if (committed.length === 0) return [];
    return floating ? [...committed, floating] : [...committed];
  };

  const pickCartesian = (screenPos) => {
    if (!screenPos) return null;

    if (scene.pickPositionSupported) {
      const p = scene.pickPosition(screenPos);
      if (Cesium.defined(p)) return p;
    }

    const ray = viewer.camera.getPickRay(screenPos);
    if (!ray) return null;

    const p = scene.globe.pick(ray, scene);
    return Cesium.defined(p) ? p : null;
  };

  const formatArea = (m2) => {
    if (!isFinite(m2) || m2 <= 0) return "0 m²";
    if (m2 < 1_000_000) return `${m2.toFixed(2)} m²`;
    return `${(m2 / 1_000_000).toFixed(3)} km²`;
  };

  const computeAreaMeters2 = (positions) => {
    if (!positions || positions.length < 3) return 0;

    const center = Cesium.BoundingSphere.fromPoints(positions).center;
    const enu = Cesium.Transforms.eastNorthUpToFixedFrame(center);
    const inv = Cesium.Matrix4.inverse(enu, new Cesium.Matrix4());

    let area2 = 0;
    for (let i = 0; i < positions.length; i++) {
      const aW = positions[i];
      const bW = positions[(i + 1) % positions.length];

      const a = Cesium.Matrix4.multiplyByPoint(inv, aW, new Cesium.Cartesian3());
      const b = Cesium.Matrix4.multiplyByPoint(inv, bW, new Cesium.Cartesian3());

      area2 += a.x * b.y - b.x * a.y;
    }
    return Math.abs(area2) * 0.5;
  };

  const ensureEntities = () => {
    if (polygonEntity) return;

    polygonEntity = viewer.entities.add({
      polygon: {
        hierarchy: new Cesium.CallbackProperty(() => {
          const pts = getLivePositions();
          if (pts.length < 3) return null;
          return new Cesium.PolygonHierarchy(pts);
        }, false),
        material: Cesium.Color.YELLOW.withAlpha(0.25),
        outline: false,
      },
    });

    outlineEntity = viewer.entities.add({
      polyline: {
        positions: new Cesium.CallbackProperty(() => {
          const pts = getLivePositions();
          if (pts.length < 2) return pts;
          return [...pts, pts[0]];
        }, false),
        width: 2,
        material: Cesium.Color.YELLOW,
        clampToGround: false,
      },
    });

    labelEntity = viewer.entities.add({
      position: new Cesium.CallbackProperty(() => {
        const pts = getLivePositions();
        if (pts.length === 0) return undefined;
        return Cesium.BoundingSphere.fromPoints(pts).center;
      }, false),
      label: {
        text: new Cesium.CallbackProperty(() => {
          const pts = getLivePositions();
          const area = computeAreaMeters2(pts);
          return `Area: ${formatArea(area)}`;
        }, false),
        font: "14px sans-serif",
        showBackground: true,
        backgroundColor: Cesium.Color.BLACK.withAlpha(0.65),
        pixelOffset: new Cesium.Cartesian2(0, -20),
        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      },
    });
  };

  const removeEntities = () => {
    try {
      if (polygonEntity) viewer.entities.remove(polygonEntity);
      if (outlineEntity) viewer.entities.remove(outlineEntity);
      if (labelEntity) viewer.entities.remove(labelEntity);
    } catch {}
    polygonEntity = null;
    outlineEntity = null;
    labelEntity = null;
  };

  const detachAllListeners = () => {
    try {
      handler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK);
      handler.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE);
      handler.removeInputAction(Cesium.ScreenSpaceEventType.RIGHT_CLICK);
    } catch {}

    try {
      handler.destroy();
    } catch {}

    window.removeEventListener("keydown", onKeyDown, true);
  };

  const resetState = () => {
    committed = [];
    floating = null;
  };

  // Always reset drawings when tool ends
  const endAndReset = () => {
    if (ended) return;
    ended = true;

    detachAllListeners();
    removeEntities();
    resetState();

    onFinish?.();
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      endAndReset();
      return;
    }

    if (e.key === "Enter") {
      if (committed.length >= 3) {
        e.preventDefault();
        endAndReset();
      }
    }
  };

  handler.setInputAction((movement) => {
    const p = pickCartesian(movement?.position);
    if (!p) return;

    ensureEntities();
    committed = [...committed, p];
  }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

  handler.setInputAction((movement) => {
    if (committed.length === 0) return;

    const p = pickCartesian(movement?.endPosition);
    if (!p) return;

    ensureEntities();
    floating = p;
  }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

  // Finish => reset + exit
  handler.setInputAction(() => {
    if (committed.length >= 3) {
      endAndReset();
    }
  }, Cesium.ScreenSpaceEventType.RIGHT_CLICK);

  window.addEventListener("keydown", onKeyDown, true);

  return {
    destroy() {
      endAndReset();
    },
  };
}
